import { useState } from "react";
import { trpc } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { fmtDateTime } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input, Select } from "../../components/ui/input";
import { THEAD_ROW } from "./base-data-shared";

/** 操作日誌檢視（admin；Phase 7）— 篩選（動作關鍵字/角色）＋offset 分頁。 */
const LIMIT = 50;
type RoleFilter = "" | "admin" | "dispatcher" | "agency" | "fleet" | "system_job";

export default function AuditLogs() {
  const { t, lang } = useLanguage();
  const [action, setAction] = useState("");
  const [userRole, setUserRole] = useState<RoleFilter>("");
  const [offset, setOffset] = useState(0);

  const q = trpc.audit.list.useQuery({
    action: action || undefined,
    userRole: userRole || undefined,
    offset,
    limit: LIMIT,
  });

  const rows = q.data?.rows ?? [];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <Input
          value={action}
          placeholder={t({ "zh-TW": "動作關鍵字（如 settlement.）", "en-US": "Action keyword", "zh-CN": "动作关键字" })}
          onChange={(e) => {
            setAction(e.target.value);
            setOffset(0);
          }}
          className="w-64"
        />
        <Select
          value={userRole}
          onChange={(e) => {
            setUserRole(e.target.value as RoleFilter);
            setOffset(0);
          }}
          className="w-40"
        >
          <option value="">{t({ "zh-TW": "全部角色", "en-US": "All roles", "zh-CN": "全部角色" })}</option>
          <option value="admin">admin</option>
          <option value="dispatcher">dispatcher</option>
          <option value="agency">agency</option>
          <option value="fleet">fleet</option>
          <option value="system_job">system_job</option>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-[13px]">
          <thead>
            <tr className={THEAD_ROW}>
              <th className="p-3">{t({ "zh-TW": "時間", "en-US": "Time", "zh-CN": "时间" })}</th>
              <th className="p-3">{t({ "zh-TW": "使用者", "en-US": "User", "zh-CN": "用户" })}</th>
              <th className="p-3">{t({ "zh-TW": "角色", "en-US": "Role", "zh-CN": "角色" })}</th>
              <th className="p-3">{t({ "zh-TW": "動作", "en-US": "Action", "zh-CN": "动作" })}</th>
              <th className="p-3">{t({ "zh-TW": "對象", "en-US": "Target", "zh-CN": "对象" })}</th>
              <th className="p-3">IP</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr><td className="p-4" colSpan={6}>…</td></tr>
            )}
            {!q.isLoading && rows.length === 0 && (
              <tr>
                <td className="p-4 text-muted-foreground" colSpan={6}>
                  {t({ "zh-TW": "無符合紀錄。", "en-US": "No records.", "zh-CN": "无符合记录。" })}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border align-top">
                <td className="whitespace-nowrap p-3">{fmtDateTime(r.createdAt, lang)}</td>
                <td className="p-3">{r.userEmail ?? `#${r.userId}`}</td>
                <td className="p-3">{r.userRole}</td>
                <td className="p-3 font-mono text-[12px]">{r.action}</td>
                <td className="p-3">{r.targetType ? `${r.targetType} #${r.targetId}` : "—"}</td>
                <td className="p-3 text-[12px] text-muted-foreground">{r.ipAddress ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>
          {t({ "zh-TW": "上一頁", "en-US": "Prev", "zh-CN": "上一页" })}
        </Button>
        <Button variant="ghost" size="sm" disabled={!q.data?.hasMore} onClick={() => setOffset(offset + LIMIT)}>
          {t({ "zh-TW": "下一頁", "en-US": "Next", "zh-CN": "下一页" })}
        </Button>
        {offset > 0 && <span className="text-[12px] text-muted-foreground">offset {offset}</span>}
      </div>
    </div>
  );
}
